import { IconCheck, IconSelector, IconSettings, IconUserPlus } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import {
  Menu,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
} from "@/components/ui/menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { usePermission } from "@/hooks/use-permission";
import { cn } from "@/lib/utils";
import { useAuthStore, type WorkspaceView } from "@/stores/auth";

/**
 * Brand-mark gradients from the Paper workspace tile. They are decorative
 * identity marks rather than semantic UI colors, so they stay raw here; a
 * workspace id hashes to one variant so each workspace keeps a stable mark.
 */
const WORKSPACE_MARKS = [
  "radial-gradient(circle farthest-corner at 16% 18% in oklab, #7FEBFF 0%, #7FEBFF00 44%), radial-gradient(circle farthest-corner at 90% 10% in oklab, #B07CFF 0%, #B07CFF00 50%), radial-gradient(circle farthest-corner at 84% 90% in oklab, #FF6FA8 0%, #FF6FA800 54%), radial-gradient(circle farthest-corner at 10% 94% in oklab, #FFB27A 0%, #FFB27A00 38%), linear-gradient(in oklab 135deg, #2F6BFF 0%, #5A3CF2 100%)",
  "radial-gradient(circle farthest-corner at 18% 18% in oklab, #FFE38A 0%, #FFE38A00 44%), radial-gradient(circle farthest-corner at 90% 14% in oklab, #FF8A5B 0%, #FF8A5B00 50%), radial-gradient(circle farthest-corner at 78% 92% in oklab, #E64AA0 0%, #E64AA000 55%), linear-gradient(in oklab 135deg, #FF6B3D 0%, #C2307A 100%)",
] as const;
const WORKSPACE_MARK_SHEEN = "linear-gradient(in oklab 165deg, #FFFFFF52 0%, #FFFFFF00 40%)";

/**
 * Sidebar workspace selector. ADP-167 gives every user exactly one Workspace
 * and the API has no switch endpoint, so the list is informational: the active
 * workspace is checked and any other membership renders disabled.
 */
export function WorkspaceSelect({
  collapsed = false,
}: {
  collapsed?: boolean;
}): React.ReactElement | null {
  const { t } = useTranslation("layout");
  const navigate = useNavigate();
  const { hasModule, hasPermission } = usePermission();
  const user = useAuthStore((state) => state.user);
  const workspaces = user?.workspaces ?? [];
  const active =
    workspaces.find((workspace) => workspace.id === user?.activeWorkspaceId) ?? workspaces[0];

  if (!active) return null;

  const canOpenSettings = hasModule("settings");
  const canInvite = hasPermission("manage_team");
  const meta = workspaceMeta(t, active);

  const trigger = (
    <button
      type="button"
      aria-label={collapsed ? t("workspace.menuLabel", { name: active.name }) : undefined}
      className={cn(
        "flex items-center rounded-md text-left outline-none transition-colors duration-150 ease-out hover:bg-state-hover focus-visible:shadow-[0_0_0_1px_var(--color-focus-ring),0_0_0_4px_var(--color-focus-halo)] data-popup-open:bg-state-hover motion-reduce:transition-none",
        collapsed ? "size-10 justify-center" : "h-[52px] w-full gap-2.5 py-2 pr-2 pl-1.5",
      )}
    >
      <WorkspaceMark id={active.id} size="lg" />
      {!collapsed ? (
        <>
          <span className="flex min-w-0 flex-1 flex-col gap-px">
            <span className="truncate text-sm font-semibold text-ink">{active.name}</span>
            <span className="truncate text-xs text-muted-foreground tabular-nums">{meta}</span>
          </span>
          <IconSelector
            className="size-3.5 shrink-0 text-muted-foreground"
            stroke={2}
            aria-hidden
          />
        </>
      ) : null}
    </button>
  );

  return (
    <Menu>
      {collapsed ? (
        <Tooltip>
          <MenuTrigger render={<TooltipTrigger render={trigger} />} />
          <TooltipContent side="right">{active.name}</TooltipContent>
        </Tooltip>
      ) : (
        <MenuTrigger render={trigger} />
      )}
      <MenuPopup
        side={collapsed ? "right" : "bottom"}
        align="start"
        className="w-[284px] rounded-md"
      >
        <MenuGroup>
          <MenuGroupLabel className="px-2.5 pt-2 pb-1.5 text-xs font-medium text-muted-foreground">
            {t("workspace.switchTitle")}
          </MenuGroupLabel>
          {workspaces.map((workspace) => {
            const isActive = workspace.id === active.id;
            return (
              <MenuItem
                key={workspace.id}
                disabled={!isActive}
                className={cn(
                  "h-12 gap-2.5 rounded-md py-0 pr-2.5 pl-2 sm:min-h-12",
                  isActive && "bg-state-selected data-highlighted:bg-state-selected",
                )}
              >
                <WorkspaceMark id={workspace.id} size="sm" />
                <span className="flex min-w-0 flex-1 flex-col gap-px">
                  <span className="truncate text-sm font-semibold text-ink">{workspace.name}</span>
                  <span className="truncate text-xs text-muted-foreground tabular-nums">
                    {workspaceMeta(t, workspace)}
                  </span>
                </span>
                <span className="flex size-4 shrink-0 items-center justify-center">
                  {isActive ? (
                    <IconCheck
                      className="size-4 text-brand opacity-100"
                      stroke={2.25}
                      aria-hidden
                    />
                  ) : null}
                </span>
              </MenuItem>
            );
          })}
        </MenuGroup>
        {canOpenSettings || canInvite ? (
          <>
            <MenuSeparator className="mx-0 my-1 bg-line" />
            <MenuGroup>
              {canOpenSettings ? (
                <WorkspaceAction
                  icon={IconSettings}
                  label={t("workspace.settings")}
                  onClick={() => void navigate({ to: "/settings", search: { tab: "workspace" } })}
                />
              ) : null}
              {canInvite ? (
                <WorkspaceAction
                  icon={IconUserPlus}
                  label={t("workspace.invite")}
                  onClick={() => void navigate({ to: "/account" })}
                />
              ) : null}
            </MenuGroup>
          </>
        ) : null}
      </MenuPopup>
    </Menu>
  );
}

function WorkspaceAction({
  icon: Icon,
  label,
  onClick,
}: {
  icon: typeof IconSettings;
  label: string;
  onClick: () => void;
}): React.ReactElement {
  return (
    <MenuItem
      onClick={onClick}
      className="h-8 gap-2.5 rounded-md py-0 pr-2.5 pl-2 text-sm text-ink sm:min-h-8 sm:text-sm"
    >
      {/* 30px lane lines the action icons up with the workspace marks above. */}
      <span className="flex h-5 w-[30px] shrink-0 items-center justify-center text-muted-foreground">
        <Icon className="size-4" stroke={1.75} aria-hidden />
      </span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
    </MenuItem>
  );
}

function WorkspaceMark({ id, size }: { id: string; size: "sm" | "lg" }): React.ReactElement {
  const mark = WORKSPACE_MARKS[markIndex(id)] ?? WORKSPACE_MARKS[0];
  return (
    <span
      aria-hidden
      className={cn(
        "relative shrink-0 overflow-clip",
        size === "lg"
          ? "size-9 rounded-md shadow-[inset_0_0_0_1px_#FFFFFF47,0_1px_2px_#1E286E29]"
          : "size-[30px] rounded-sm shadow-[inset_0_0_0_1px_#FFFFFF47]",
      )}
      style={{ backgroundImage: `${WORKSPACE_MARK_SHEEN}, ${mark}` }}
    >
      {size === "lg" ? (
        <span className="absolute top-1.5 left-[7px] h-1.5 w-2.5 rounded-full bg-white/55 blur-[3px]" />
      ) : null}
    </span>
  );
}

function markIndex(id: string): number {
  let hash = 0;
  for (const char of id) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return hash % WORKSPACE_MARKS.length;
}

function workspaceMeta(t: TFunction<"layout">, workspace: WorkspaceView): string {
  return `${t(`workspace.roles.${workspace.role}`)} · ${t("workspace.shopCount", { count: workspace.shopCount })}`;
}
